import { add, sub, ZERO, type Decimal } from '../core/decimal.ts';
import type { BalanceSnapshot, Id, Transaction } from '../types/domain.ts';

export interface NetWorthParts {
  readonly cash: Decimal;
  readonly investments: Decimal;
  readonly propertyEquity: Decimal;
  readonly assetEquity: Decimal;
  readonly debt: Decimal;
}

/** NetWorth.total: cash + investments + propertyEquity + assetEquity − debt, left to right. */
export const netWorthTotal = (p: NetWorthParts): Decimal =>
  sub(add(add(add(p.cash, p.investments), p.propertyEquity), p.assetEquity), p.debt);

/** BalanceSnapshot.netWorth: cash + investments + savings + property equity + asset value (0 when unrecorded) − debt. */
export const snapshotNetWorth = (b: BalanceSnapshot): Decimal =>
  sub(add(add(add(add(b.cash, b.investments), b.savings), b.propertyEquity), b.assetValue ?? ZERO), b.debt);

/**
 * NetWorth.countsAsDebt: whether a debt is in the debt line of net worth (R3). A debt linked to
 * a property is a mortgage, netted in that property's equity unless treated as debt; every
 * other debt (unlinked, finance on a vehicle or other non-property asset, an unknown link) is.
 */
export const countsAsDebt = (debt: Transaction, propertyIds: ReadonlySet<Id>, treatedAsDebt: ReadonlySet<Id> = new Set()): boolean =>
  debt.linkedAssetId === null || !propertyIds.has(debt.linkedAssetId) || treatedAsDebt.has(debt.id);
